import React from "react";
// Components
import AccordionCustom from "@components/Accordion/AccordionCustom";
import NewLayout from "@components/new-ui/Layout";
import ConversationTypes from "@components/WAPricingCalculator/ConversationTypes";
import PricingCalculator from "@components/WAPricingCalculator/PricingCalculator";
import YellowCTAhref from "@components/yellowCTAhref";
//React icons
import schema from "@components/faqSchema/whatsapp-conversation-pricing-calculator-faqSchema.json";
import Image from "next/image";
import Head from "next/head";
import BlogOrCaseStudy from "@components/BlogOrCaseStudy";
import SpotlightSection from "@components/new-ui/SpotlightSection";

const BenefitsCardContent = [
    {
        image: {
            source: "https://assets.gallabox.com/gb-home/wa-pricing-calculator/banner-image/customers.svg",
            alt: "customer-icon",
        },
        title: "Be there for your customers",
        description: "Helping your customers is always a priority. That's why we do not charge for service messages, or for utility messages businesses send in response to users.",
    },
    {
        image: {
            source: "https://assets.gallabox.com/gb-home/wa-pricing-calculator/banner-image/rocket.svg",
            alt: "rocket-icon",
        },
        title: "Get rewarded as you grow",
        description: "As businesses send more messages on WhatsApp, they can unlock more attractive pricing via our volume tiers.",
    },
    {
        image: {
            source: "https://assets.gallabox.com/gb-home/wa-pricing-calculator/banner-image/layout.svg",
            alt: "layout-icon",
        },
        title: "Leverage free entry points",
        description:
            "When customers send you a message from an Ad that clicks to WhatsApp or Facebook Page call-to-action button, for the following 3 days (72 hours), all of your messages are not charged.",
    },
];

const RELATED_CASE_STUDIES = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/wa-pricing-calculator/banner-image/qr-code.png",
        bannerTitle: "Get More Leads and Sales with a WhatsApp QR Code Generator",
        bannerDescription:
            "Make engagement problems a thing of the past with a WhatsApp QR Code Generator. Let your customers (current and potential) reach you effortlessly, capturing every possible opportunity.",
        redirectUrl: "/whatsapp-qr-code-generator",
        buttonText: "Try Now",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/wa-pricing-calculator/banner-image/whatsapp-links.png",
        bannerTitle: "Connect with Your Customers Instantly Using WhatsApp Links",
        redirectUrl: "/whatsapp-link-generator",
        buttonText: "Try Now",
        bannerDescription:
            "Boost your sales and build meaningful connections using Gallabox's WhatsApp Link Generator. Increase customer satisfaction and improve business impact by giving your customers a direct line to reach you!",
    },
];

export interface AccordionItemsProps {
    question: string;
    answer: any;
}

const accordionCustomInputItems: AccordionItemsProps[] = [
    {
        question: "What is a WABA (WhatsApp Business Account)?",
        answer: (
            <>
                A WhatsApp Business Account (WABA) is a professional profile specifically designed for businesses, streamlining customer interaction and service delivery. It offers
                unique features such as auto-responses, pre-approved templates, and a catalogue option for showcasing products or services, enhancing customer communication
                experience.
            </>
        ),
    },
    {
        question: "How much does it cost to use the WhatsApp Business API?",
        answer: (
            <>
                At Gallabox, we offer 3 different plans for businesses who want to use the WhatsApp Business API.To compare our{" "}
                <a className="text-blue-500 hover:text-blue-600" style={{ textDecoration: "none" }} href="/pricing">
                    WhatsApp Business API pricing
                </a>
                .
                <br />
                Besides the Pro Plan, we also provide flexible plans with additional headcount and customized integrations that can be tailored to your specific needs. Please
                don&apos;t hesitate to get in touch to learn more about our offerings!
            </>
        ),
    },
    {
        question: "What are the different message categories available in WhatsApp Business?",
        answer: (
            <>
                WhatsApp Business Platform has four message categories. <br /> <span className="text-gray-700 font-bold">1. Marketing </span>
                <br /> Businesses can send promotional messages about their products or services to opted-in customers. Any message that doesn&apos;t fit into Utility or
                Authentication is categorized as Marketing.
                <br /> <span className="text-gray-700 font-bold"> 2. Authentication </span> <br /> Businesses can send one-time passcodes to verify users during account
                registration, recovery, or security issues.
                <br /> <span className="text-gray-700 font-bold"> 3. Utility </span> <br /> Businesses can send transaction-related messages like purchase notifications or billing
                statements to opted-in users.
                <br /> <span className="text-gray-700 font-bold"> 4. Service </span> <br /> User-initiated discussions to resolve user inquiries fall under the Service Message
                category.
            </>
        ),
    },
];

const WAPricingCalculator: React.FC = () => {
    return (
        <NewLayout
            title="WhatsApp API Pricing Calculator | Get Instant Cost Estimates"
            description="Use the Gallabox WhatsApp Business API Pricing Calculator to estimate conversation costs, plan your budget, and optimize business communication."
            canonical="https://gallabox.com/whatsapp-conversation-pricing-calculator"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/wa-pricing-calculator/og.png" }}
            keywords="Conversation pricing calculator, Whatsapp message calculator, WhatsApp business API cost"
            schema={{
                heroOrOgImageAlt: "whatsapp-conversation-pricing-calculator-image",
                heroOrOgImageDescription: "WhatsApp Business API Pricing Calculator to Estimate Costs Instantly",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/new-zapier/wa-w-whiteborder.svg",
                faq: accordionCustomInputItems,
                softwareFeatureName: "whatsapp-conversation-pricing-calculator",
            }}
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Head>
            <section style={{ backgroundColor: "#E6EEFF" }}>
                <div className="md:mt-10 pt-20 pb-20 mx-auto max-w-7xl flex md:flex-row flex-col gap-10 md:gap-x-10">
                    <div className="flex flex-col md:gap-5 gap-4 md:w-1/2 w-full px-4 md:px-4 ">
                        <p className="text-base font-semibold mb-3 md:block" style={{ color: "#3364FA" }}>
                            WhatsApp Business API Pricing Calculator
                        </p>
                        <h1 className="gray-900 font-bold md:text-6xl text-6xl md:leading-none" style={{ fontFamily: "Degular Variable" }}>
                            WhatsApp Business API Pricing Calculator to Estimate Costs Instantly
                        </h1>
                        <p className="text-gray-700 md:text-xl md:mt-5 mt-4 text-left md:text-left">
                            Finally, here’s a straightforward and precise strategy to forecast your marketing expenses with WhatsApp Pricing Calculator. You can now prioritise your
                            campaigns and reveal potential ways to cut costs and save spend.
                        </p>
                        <div className="px-6 py-6 bg-white flex flex-col gap-2 rounded-xl mt-12 md:mt-52">
                            <p className="text-gray-700 font-semibold text-center md:text-left flex gap-1 text-lg">
                                <Image
                                    height={24}
                                    width={24}
                                    src={"/note.png"}
                                    alt="notes-icon"
                                    style={{ width: 24, height: 24, objectFit: "contain" }} // or objectFit: "cover"
                                />
                                Keep in mind:
                            </p>
                            <p className="text-gray-700 text-center text-lg md:text-left">
                                The prices reflect Meta&apos;s rates, but there may be variations depending on your BSP. Exchange rates and gateway fees can cause the final cost to
                                differ
                            </p>
                        </div>
                    </div>
                    <div className="w-fit lg:mx-4 md:mx-4 mx-4 bg-white rounded-xl border border-solid border-gray-100">
                        <PricingCalculator />
                    </div>
                </div>
            </section>

            <div className="mb-20">
                <div className="w-fit lg:mx-auto mx-4">
                    <ConversationTypes
                        title="Different Message Types"
                        description="WhatsApp messages can be classified into four main types: each has different rates, which also varies depending on the market in which you operate."
                    />
                </div>
            </div>

            <div style={{ backgroundColor: "#E6EEFF" }}>
                <SpotlightSection headerText="Optimize Your Marketing Spend with Accurate Forecast" data={BenefitsCardContent} />
            </div>

            <div className="lg:max-w-7xl lg:mx-auto w-full text-center">
                <BlogOrCaseStudy
                    mainTitle={
                        <h3 className="text-6xl text-gray-900 text-left font-bold" style={{ fontFamily: "Degular Display" }}>
                            Free Tools to Grow Your Business on WhatsApp
                        </h3>
                    }
                    bannerContent={RELATED_CASE_STUDIES}
                />
            </div>

            <div className="lg:mt-20 mt-14 mb-10">
                <section className="max-w-7xl mx-auto px-4">
                    <div className="flex flex-col justify-center lg:gap-4 gap-2 items-start pb-6">
                        <h3 className="lg:text-6xl text-6xl md:px-0 lg:text-left md:text-center text-left font-bold text-gray-900" style={{ fontFamily: "Degular Display" }}>
                            WhatsApp Business Calculator: <br /> Frequently Asked Questions
                        </h3>
                    </div>
                    <AccordionCustom title="WhatsApp Pricing Calculator" items={accordionCustomInputItems} />
                </section>
            </div>

            <div className="py-10 md:pb-5 md:mx-auto mx-2">
                <YellowCTAhref
                    cardStyles={{ cardColor: "linear-gradient(128deg, #F3FFF4 1.19%, #ADE2E8 99.94%)", cardTextColor: "black", buttonBackgroundColor: "bg-blue-color" }}
                    buttonText="Get Started Today"
                    customText={
                        <h3 className="text-5xl text-left md:text-center md:text-5xl px-0 md:px-20 font-bold text-black" style={{ fontFamily: "Degular Display" }}>
                            Put your pipeline generation on autopilot with Gallabox!
                        </h3>
                    }
                />
            </div>
        </NewLayout>
    );
};

export default WAPricingCalculator;
